import React from 'react';

interface AthletesTabProps {
    selectedTeam: any;
    rankings: any;
    isDark: boolean;
    handleAthleteDoubleClick: (athleteId: number) => void;
}

// Add color helpers for recovery, sleep, and strain:
function getRecoveryColor(value: number) {
  if (value >= 67) return '#67AEE6'; // High
  if (value >= 34) return '#F5D76E'; // Medium
  return '#EF4444'; // Low
}
function getSleepColor() {
  return '#7BA1BB';
}
function getStrainColor() {
  return '#0093E7';
}

const AthletesTab: React.FC<AthletesTabProps> = ({
    selectedTeam,
    rankings,
    isDark,
    handleAthleteDoubleClick
}) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {selectedTeam.recovery_data?.athletes_data.map((athlete: any) => {
                // Get real data from athlete's raw data, sorted by date to ensure latest is most recent
                const recoveryData = athlete.recovery_data?.filter(((data: any) => data && data.recovery_score))
                    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
                const sleepData = selectedTeam.sleep_data?.athletes_data
                    .find((a: any) => a.athlete_name === athlete.athlete_name)?.sleep_data
                    ?.filter(((data: any) => data && data.total_duration_hours))
                    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
                const activityData = selectedTeam.activity_data?.athletes_data
                    .find((a: any) => a.athlete_name === athlete.athlete_name)?.activity_data
                    ?.filter(((data: any) => data && data.strain_score))
                    .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];

                // Calculate real stats from actual data with proper rounding
                const cleanRecoveryStats = {
                    average: recoveryData.length > 0 ? Math.round((recoveryData.reduce((sum: number, d: any) => sum + d.recovery_score, 0) / recoveryData.length) * 10) / 10 : 0,
                    latest: recoveryData.length > 0 ? Math.round(recoveryData[recoveryData.length - 1].recovery_score * 10) / 10 : 0,
                    dataPoints: recoveryData.length
                };

                const cleanSleepStats = {
                    averageQuality: sleepData.length > 0 ? Math.round(sleepData.reduce((sum: number, d: any) => sum + (d.sleep_quality_score || 0), 0) / sleepData.length) : 0,
                    averageDuration: sleepData.length > 0 ? Math.round((sleepData.reduce((sum: number, d: any) => sum + d.total_duration_hours, 0) / sleepData.length) * 10) / 10 : 0,
                    latestQuality: sleepData.length > 0 ? Math.round(sleepData[sleepData.length - 1].sleep_quality_score || 0) : 0,
                    latestDuration: sleepData.length > 0 ? Math.round(sleepData[sleepData.length - 1].total_duration_hours * 10) / 10 : 0,
                    dataPoints: sleepData.length
                };

                const cleanActivityStats = {
                    averageStrain: activityData.length > 0 ? Math.round((activityData.reduce((sum: number, d: any) => sum + d.strain_score, 0) / activityData.length) * 10) / 10 : 0,
                    latestStrain: activityData.length > 0 ? Math.round(activityData[activityData.length - 1].strain_score * 10) / 10 : 0,
                    dataPoints: activityData.length
                };

                return (
                    <div
                        key={athlete.athlete_id}
                        className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer group`}
                        onDoubleClick={() => handleAthleteDoubleClick(athlete.athlete_id)}
                    >
                        <div className="flex flex-col h-full">
                            {/* Athlete Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className={`w-12 h-12 ${isDark ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-gradient-to-r from-purple-500 to-blue-500'} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                                        {athlete.athlete_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="text-lg font-semibold theme-text-primary group-hover:text-blue-600 transition-colors">
                                            {athlete.athlete_name}
                                        </h3>
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2 h-2 rounded-full ${athlete.whoop_connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                            <p className="text-sm theme-text-secondary">
                                                {athlete.whoop_connected ? 'Connected' : 'No Data'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            {athlete.whoop_connected && (
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    {/* Recovery */}
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Recovery</span>
                                            <span className="text-xs">💚</span>
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getRecoveryColor(cleanRecoveryStats.latest) }}>{cleanRecoveryStats.latest > 0 ? `${cleanRecoveryStats.latest.toFixed(0)}%` : 'N/A'}</span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            Avg: <span style={{ color: getRecoveryColor(cleanRecoveryStats.average) }}>{cleanRecoveryStats.average > 0 ? `${cleanRecoveryStats.average.toFixed(0)}%` : 'N/A'}</span>
                                        </div>
                                    </div>

                                    {/* Sleep */}
                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Sleep</span>
                                            <span className="text-xs">😴</span>
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getSleepColor() }}>
                                                {cleanSleepStats.latestDuration > 0 ? `${cleanSleepStats.latestDuration.toFixed(1)}h` : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            Avg: {cleanSleepStats.averageDuration > 0 ? `${cleanSleepStats.averageDuration.toFixed(1)}h` : 'N/A'}
                                        </div>
                                    </div>

                                    {/* Strain */}
                                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Strain</span>
                                            <span className="text-xs">🔥</span>
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getStrainColor() }}>{cleanActivityStats.latestStrain > 0 ? `${cleanActivityStats.latestStrain.toFixed(1)}` : 'N/A'}</span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            Avg: <span style={{ color: getStrainColor() }}>{cleanActivityStats.averageStrain > 0 ? `${cleanActivityStats.averageStrain.toFixed(1)}` : 'N/A'}</span>
                                        </div>
                                    </div>

                                    {/* Data Points */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Data</span>
                                            <span className="text-xs">📊</span>
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            {Math.max(cleanRecoveryStats.dataPoints || 0, cleanSleepStats.dataPoints || 0, cleanActivityStats.dataPoints || 0)}
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            days
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* No Data Message */}
                            {!athlete.whoop_connected && (
                                <div className="bg-gray-100 dark:bg-gray-700/50 rounded-xl p-4 mb-4 text-center">
                                    <div className="text-2xl mb-2">📱</div>
                                    <p className="text-sm theme-text-secondary">No wearable data available</p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="mt-auto">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAthleteDoubleClick(athlete.athlete_id);
                                    }}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                                >
                                    View Details
                                </button>
                            </div>

                            {/* Double-click hint */}
                            <div className="mt-2 text-center">
                                <p className="text-xs theme-text-secondary opacity-50">
                                    Double-click for AI insights
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* Empty State */}
            {(!selectedTeam.recovery_data?.athletes_data || selectedTeam.recovery_data.athletes_data.length === 0) && (
                <div className={`col-span-full ${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center`}>
                    <div className={`w-24 h-24 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold theme-text-primary mb-4">No Athletes Found</h3>
                    <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                        This team doesn't have any athletes with data for the selected time period. Athletes need to connect their Whoop devices to appear here.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AthletesTab; 